import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { UsagePiece } from '@/services/usagePiecesService';

/**
 * Quality-score distribution. Three lone numbers (mean/median/mode) don't need a
 * chart, so they're stat tiles; the SPREAD is what a chart shows, so the scores
 * are bucketed into a histogram with the mean marked. Bars are coloured by band
 * (good/ok/low) using the FIXED pace tokens, never the client-themed brand hue.
 */
const BINS: { label: string; min: number; max: number; color: string }[] = [
  { label: '<60', min: -Infinity, max: 60, color: 'var(--color-pace-over)' },
  { label: '60–69', min: 60, max: 70, color: 'var(--color-pace-warn)' },
  { label: '70–79', min: 70, max: 80, color: 'var(--color-pace-warn)' },
  { label: '80–89', min: 80, max: 90, color: 'var(--color-pace-good)' },
  { label: '90–100', min: 90, max: Infinity, color: 'var(--color-pace-good)' },
];

function mean(xs: number[]): number {
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}
function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
function mode(xs: number[]): number {
  const counts = new Map<number, number>();
  let best = xs[0];
  let bestN = 0;
  for (const x of xs) {
    const n = (counts.get(x) ?? 0) + 1;
    counts.set(x, n);
    if (n > bestN) {
      bestN = n;
      best = x;
    }
  }
  return best;
}

export function UsageQualityDistribution({ pieces }: { pieces: UsagePiece[] }) {
  const scores = useMemo(
    () => pieces.map((p) => p.quality_score).filter((s): s is number => s !== null && s !== undefined),
    [pieces],
  );

  const stats = useMemo(() => {
    if (scores.length === 0) return null;
    return { mean: mean(scores), median: median(scores), mode: mode(scores), n: scores.length };
  }, [scores]);

  const bars = useMemo(
    () =>
      BINS.map((b) => ({
        label: b.label,
        color: b.color,
        count: scores.filter((s) => s >= b.min && s < b.max).length,
      })),
    [scores],
  );

  if (!stats) {
    return (
      <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">
        No quality scores yet this cycle.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <QualityStat label="Mean" value={Math.round(stats.mean)} />
        <QualityStat label="Median" value={Math.round(stats.median)} />
        <QualityStat label="Mode" value={stats.mode} />
      </div>
      <div className="h-40 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
            <CartesianGrid vertical={false} stroke="var(--color-border)" strokeOpacity={0.5} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--color-border)' }}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={32}
            />
            <Tooltip
              cursor={{ fill: 'var(--color-muted-foreground)', fillOpacity: 0.08 }}
              contentStyle={{ borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12 }}
              formatter={(value: number) => [`${value} piece${value === 1 ? '' : 's'}`, 'Count']}
            />
            <ReferenceLine
              x={BINS.find((b) => stats.mean >= b.min && stats.mean < b.max)?.label}
              stroke="var(--color-foreground)"
              strokeDasharray="4 3"
              label={{ value: 'mean', position: 'top', fontSize: 10, fill: 'var(--color-muted-foreground)' }}
            />
            <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={48}>
              {bars.map((b) => (
                <Cell key={b.label} fill={b.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Distribution of quality scores across {stats.n} scored piece{stats.n === 1 ? '' : 's'}.
      </p>
    </div>
  );
}

function QualityStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-warm-bg/50 p-3 text-center">
      <div className="font-display text-2xl font-semibold tabular-nums text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
